import { reportOperationError } from '../../../lib/observability.ts'
import { UserFacingError } from '../../../utils/UserFacingError.ts'
import { loadOpenRestaurantOrders } from '../../tables/service'
import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'
import { sileo } from 'sileo'
import {
  clearSaleLedger,
  clearSessionTickets,
  getSessionTickets,
  getOfflineQueue,
  saveCachedCashSession,
  saveSaleLedger,
  saveSessionTickets,
} from '../../../lib/offlineStore'
import {
  loadSalesLedgerFromSupabase,
  loadSessionTicketFromSupabase,
  summarizeSales,
} from '../../../services/posService'
import type {
  CashClosedPayload,
  CashClosingRecord,
  CashMovement,
  CashMovementType,
  CashSession,
  SaleRecord,
  SessionTicketRecord,
  TenantContext,
} from '../../../types'
import { getReadableError } from '../../../utils/errors'
import { resolveLocalFiscalMode } from '../../fiscal/local/tenantMode.ts'
import { loadFiscalInstallation } from '../../fiscal/local/installation.ts'
import { findLocalFiscalEntryByTicket } from '../../fiscal/local/localLedger.ts'
import { printPayloadWithLocalFiscal } from '../../fiscal/local/posInvoice.ts'
import { usePrintAgentScope } from '../../local-printing/hooks/usePrintAgentScope'
import { useCashlogyScope } from '../../local-printing/cashlogy/useCashlogyScope'
import { loadActiveCashlogyCashBalance } from '../../local-printing/cashlogy/cashlogyCashBalance'
import { cashClosingRequestId } from '../../local-printing/services/cashClosingPrintMapper'
import { checkUnknownClosingJob, nextCashClosingCopyNumber } from '../services/cashClosingPrintRecovery'
import { printCashClosing } from '../../local-printing/services/printCashClosing'
import { usePrintAgentStore } from '../../local-printing/store/usePrintAgentStore'
import {
  mergeRemoteTicketPrintStates,
  printTicket,
} from '../../local-printing/services/printTicket'
import {
  closeCashSession as closeCashSessionLifecycle,
  joinCashSession as joinCashSessionLifecycle,
  openCashSession as openCashSessionLifecycle,
} from '../services/cashSessionLifecycle'
import { useActiveCashSession } from './useActiveCashSession'
import { useCashRegisterOptions } from './useCashRegisterOptions'
import { useCashTicketActions } from './useCashTicketActions'
import { getClosedCashState } from '../services/cashState'
import { loadLocalSessionSales } from '../services/localSessionTickets.ts'
import {
  createCashMovement as createCashMovementRequest,
  loadCashClosing,
  loadCashClosingHistory,
  loadCashlogyStackerCollectionsTotal,
  loadCashMovements,
  recordCashClosingPrintResult,
  subscribeToCashMovements,
} from '../service'

type Options = {
  context: TenantContext | null
  isOnline: boolean
  onError: (message: string | null) => void
  refreshPendingCount: () => void
  setBusy: (busy: boolean) => void
  subtractProductSalesStats: (lines: Array<{ productId: string; quantity: number; lineTotalCents: number }>) => void
  onConfirmedSale?: (ticket: SessionTicketRecord) => void
  syncPendingEvents: () => Promise<void>
}

export function useCashSession(options: Options) {
  const onConfirmedSale = options.onConfirmedSale
  const [session, setSession] = useState<CashSession | null>(null)
  const [ledger, setLedger] = useState<SaleRecord[]>([])
  const [movements, setMovements] = useState<CashMovement[]>([])
  const [tickets, setTicketsState] = useState<SessionTicketRecord[]>([])
  const ticketsRef = useRef<SessionTicketRecord[]>([])
  const saleRefreshGenerationRef = useRef(0)
  const saleRefreshScopeRef = useRef('')
  const saleRefreshScope = `${options.context?.tenantId}:${options.context?.venueId}:${options.context?.deviceId}:${options.context?.userId}:${session?.id}`
  saleRefreshScopeRef.current = saleRefreshScope
  const [closeModalOpen, setCloseModalOpen] = useState(false)
  const [openOrderCount, setOpenOrderCount] = useState(0)
  const [movementModalOpen, setMovementModalOpen] = useState(false)
  const [movementSaving, setMovementSaving] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [closingHistoryOpen, setClosingHistoryOpen] = useState(false)
  const [cashClosings, setCashClosings] = useState<CashClosingRecord[]>([])
  const [completedClosing, setCompletedClosing] = useState<CashClosingRecord | null>(null)
  const [printingClosingId, setPrintingClosingId] = useState<string | null>(null)
  const [cashlogyClosingCashCents, setCashlogyClosingCashCents] = useState<number | null>(null)
  const [cashlogyClosingStackerCollectionsCents, setCashlogyClosingStackerCollectionsCents] = useState(0)
  const cashContext = options.context
  const isOnline = options.isOnline
  const reportError = options.onError
  const setGlobalBusy = options.setBusy
  const syncPendingEvents = options.syncPendingEvents
  usePrintAgentScope(cashContext)
  useCashlogyScope(cashContext, session?.id ?? null)

  useEffect(() => {
    if (!cashContext || !session || isOnline) return
    let active = true
    void loadLocalSessionSales(cashContext, session).then(sales => {
      if (!active || saleRefreshScopeRef.current !== saleRefreshScope) return
      setLedger(current => [...new Map([...sales, ...current].map(sale => [sale.id, sale])).values()])
    }).catch(error => {
      if (!active) return
      reportOperationError(error, { operation: 'cash.session', step: 'restore_local_sales', recoverable: true })
      reportError('No se han podido recuperar todas las ventas locales. Revisa el almacenamiento antes de cerrar la caja.')
    })
    return () => { active = false }
  }, [cashContext, isOnline, reportError, saleRefreshScope, session])

  useEffect(() => {
    if (!cashContext || !session) {
      setMovements([])
      setMovementModalOpen(false)
      return undefined
    }
    setMovements((current) => current.some((movement) => movement.cashSessionId !== session.id) ? [] : current)
    if (!isOnline) return undefined
    let active = true
    let refreshVersion = 0
    const refresh = async () => {
      const requestVersion = ++refreshVersion
      try {
        const next = await loadCashMovements(cashContext, session.id)
        if (!active || requestVersion !== refreshVersion) return
        setMovements(next)
      } catch (error) {
        if (active && requestVersion === refreshVersion) reportError(getReadableError(error, { operation: 'cash.session' }))
      }
    }
    void refresh()
    const unsubscribe = subscribeToCashMovements(session.id, () => void refresh())
    return () => {
      active = false
      unsubscribe()
    }
  }, [cashContext, isOnline, reportError, session])

  const setTickets = useCallback((value: SetStateAction<SessionTicketRecord[]>) => {
    const next = typeof value === 'function' ? value(ticketsRef.current) : value
    ticketsRef.current = next
    setTicketsState(next)
  }, [])

  const persistSession = useCallback((nextSession: CashSession | null) => {
    setSession(nextSession)
    if (options.context) saveCachedCashSession(options.context, nextSession)
  }, [options.context])

  const persistLedger = useCallback((nextLedger: SaleRecord[]) => {
    setLedger(nextLedger)
    if (options.context) saveSaleLedger(options.context, nextLedger)
  }, [options.context])

  const persistTickets = useCallback((nextTickets: SessionTicketRecord[]) => {
    setTickets(nextTickets)
    if (options.context && session) saveSessionTickets(options.context, session.id, nextTickets)
  }, [options.context, session, setTickets])

  const updateTicketPrintState = useCallback((
    saleId: string,
    patch: Partial<Pick<SessionTicketRecord,
      'printStatus' | 'printJobId' | 'printRequestId' | 'printedAt' | 'printErrorCode' | 'printAttempts'>>,
  ) => {
    const next = ticketsRef.current.map((ticket) => ticket.id === saleId ? { ...ticket, ...patch } : ticket)
    setTickets(next)
    if (options.context && session) saveSessionTickets(options.context, session.id, next)
  }, [options.context, session, setTickets])

  const mergeRemotePrintStates = useCallback(
    (remoteTickets: SessionTicketRecord[]) => mergeRemoteTicketPrintStates(ticketsRef.current, remoteTickets),
    [],
  )

  const printSale = useCallback(async (
    payload: SessionTicketRecord['payload'],
    printOptions: { isReprint?: boolean; copyNumber?: number; cashDrawerAlreadyRequested?: boolean; fiscalMode?: 'legacy' | 'verifactu' } = {},
  ) => {
    if (!options.context) return
    const printState = usePrintAgentStore.getState()
    // Copies use the original persisted snapshot, including retired installations.
    if (!printOptions.isReprint && printState.token && printState.selectedPrinterId && (payload.localFiscal
      || (printOptions.fiscalMode !== 'legacy' && await resolveLocalFiscalMode(options.context.tenantId) === 'production'))) {
      const installation = await loadFiscalInstallation(options.context, { cashRegisterId: payload.ticket.cashRegisterId })
      const entry = await findLocalFiscalEntryByTicket(installation, payload.ticket.id)
      if (!entry) {
        if (!printOptions.isReprint || !payload.fiscal || payload.localFiscal) {
          throw new UserFacingError('No se puede imprimir una factura sin su registro fiscal local.')
        }
      } else payload = printPayloadWithLocalFiscal(payload, entry)
    }
    if (!printOptions.isReprint && !ticketsRef.current.some((ticket) => ticket.id === payload.sale.id)) {
      persistTickets([{
         id: payload.sale.id,
         ticketNumber: payload.ticket.ticketNumber,
         cashSessionId: payload.sale.cashSessionId,
        paymentMethod: payload.sale.paymentMethod,
        totalCents: payload.sale.totalCents,
        createdAt: payload.sale.createdAt,
        status: 'active',
        payload,
        printStatus: 'not_requested',
        printAttempts: 0,
      }, ...ticketsRef.current])
    }
    await printTicket({
      cashSession: session,
      context: options.context,
      payload,
      tickets: ticketsRef.current,
      updateTicketPrintState,
      options: printOptions,
    })
  }, [options.context, persistTickets, session, updateTicketPrintState])

  const handleClosedRemotely = useCallback(() => {
    if (options.context) {
      saveCachedCashSession(options.context, null)
      clearSaleLedger(options.context)
      if (session) clearSessionTickets(options.context, session.id)
    }
    const closed = getClosedCashState()
    setSession(closed.session)
    setLedger([...closed.ledger])
    setTickets([...closed.tickets])
    setCloseModalOpen(false)
    setCashlogyClosingCashCents(null)
    setCashlogyClosingStackerCollectionsCents(0)
    setHistoryOpen(false)
    options.onError('La caja con la que estabas trabajando se ha cerrado.')
  }, [options, session, setTickets])

  const cashOptions = useCashRegisterOptions({
    context: options.context,
    currentSession: session,
    isOnline: options.isOnline,
    onClosedRemotely: handleClosedRemotely,
  })

  const handleActiveSessionChanged = useCallback(async (
    nextSession: CashSession | null,
    previousSession: CashSession | null,
  ) => {
    if (!options.context) return
    if (previousSession) clearSessionTickets(options.context, previousSession.id)
    setSession(nextSession)
    saveCachedCashSession(options.context, nextSession)
    setCloseModalOpen(false)
    setCashlogyClosingCashCents(null)
    setCashlogyClosingStackerCollectionsCents(0)
    setHistoryOpen(false)
    if (!nextSession) {
      setLedger([])
      clearSaleLedger(options.context)
      setTickets([])
      options.onError('La caja se ha cerrado desde otro dispositivo. Abre una nueva caja para continuar.')
      return
    }
    const remoteLedger = await loadSalesLedgerFromSupabase(options.context, nextSession.id)
    setLedger(remoteLedger)
    saveSaleLedger(options.context, remoteLedger)
    setTickets(getSessionTickets(options.context, nextSession.id))
    options.onError(null)
  }, [options, setTickets])

  useActiveCashSession({
    context: options.context,
    isOnline: options.isOnline,
    onChanged: handleActiveSessionChanged,
    onError: (message) => options.onError(message),
  })

  const refreshConfirmedSale = useCallback(async (ticketId: string, missingTicketTitle: string, shouldPrint = true) => {
    if (!options.context || !session) return
    const generation = ++saleRefreshGenerationRef.current
    const [nextLedger, remoteTicket] = await Promise.all([
      loadSalesLedgerFromSupabase(options.context, session.id),
      loadSessionTicketFromSupabase(options.context, session.id, ticketId),
    ])
    if (saleRefreshScopeRef.current !== saleRefreshScope) return
    if (generation === saleRefreshGenerationRef.current) persistLedger(nextLedger)
    const confirmedTicket = remoteTicket ? mergeRemotePrintStates([remoteTicket])[0] : null
    if (confirmedTicket) {
      const alreadyKnown = ticketsRef.current.some((ticket) => ticket.payload.ticket.id === confirmedTicket.payload.ticket.id)
      persistTickets([
        confirmedTicket,
        ...ticketsRef.current.filter((ticket) => (
          ticket.id !== confirmedTicket.id
          && ticket.payload.ticket.id !== confirmedTicket.payload.ticket.id
        )),
      ])
      if (!alreadyKnown && confirmedTicket.status === 'active') onConfirmedSale?.(confirmedTicket)
    }
    if (confirmedTicket && shouldPrint) void printSale(confirmedTicket.payload)
    else if (!confirmedTicket) sileo.warning({ title: missingTicketTitle, description: 'No se ha podido recuperar el ticket confirmado.' })
  }, [mergeRemotePrintStates, options.context, onConfirmedSale, persistLedger, persistTickets, printSale, session, saleRefreshScope])

  const refreshLedger = useCallback(async () => {
    if (!cashContext || !session || !isOnline) return ledger
    await syncPendingEvents()
    const nextLedger = await loadSalesLedgerFromSupabase(cashContext, session.id)
    persistLedger(nextLedger)
    return nextLedger
  }, [cashContext, isOnline, ledger, persistLedger, session, syncPendingEvents])
  const ticketActions = useCashTicketActions({
    context: options.context,
    cashSession: session,
    isOnline: options.isOnline,
    tickets,
    ledger,
    syncPendingEvents: options.syncPendingEvents,
    refreshPendingCount: options.refreshPendingCount,
    persistTickets,
    persistLedger,
    mergeRemotePrintStates,
    printTicket: printSale,
    subtractProductSalesStats: options.subtractProductSalesStats,
    setBusy: options.setBusy,
    setError: options.onError,
    setHistoryOpen,
  })

  const registerMovement = useCallback(async (input: {
    type: CashMovementType
    amountCents: number
    notes: string
    requestId: string
  }) => {
    const context = options.context
    const activeSession = session
    if (!context || !activeSession || !options.isOnline) {
      throw new Error('Los movimientos de caja requieren una caja abierta y conexión.')
    }
    if (!context.canManageCash && !['manager', 'owner'].includes(context.role)) {
      throw new UserFacingError('No tienes permiso para gestionar movimientos de caja.')
    }
    setMovementSaving(true)
    try {
      const movement = await createCashMovementRequest(context, {
        cashSessionId: activeSession.id,
        type: input.type,
        amountCents: input.amountCents,
        notes: input.notes.trim(),
        requestId: input.requestId,
      })
      setMovements((current) => current.some((item) => item.id === movement.id)
        ? current.map((item) => item.id === movement.id ? movement : item)
        : [...current, movement].sort((left, right) => left.createdAt.localeCompare(right.createdAt)))
      return movement
    } finally {
      setMovementSaving(false)
    }
  }, [options.context, options.isOnline, session])

  const open = useCallback(async (registerId: string, openingFloatCents: number) => {
    if (!options.context?.canOpenCashSession || !options.isOnline) return
    options.setBusy(true)
    options.onError(null)
    try {
      const cashlogyBalance = await loadActiveCashlogyCashBalance()
      const effectiveOpeningFloatCents = cashlogyBalance?.totalCents ?? openingFloatCents
      const nextSession = await openCashSessionLifecycle(options.context, registerId, effectiveOpeningFloatCents)
      persistSession(nextSession)
      setCompletedClosing(null)
      persistLedger([])
      setTickets([])
      saveSessionTickets(options.context, nextSession.id, [])
      await cashOptions.refresh(options.context)
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'cash.session', step: 'open' }))
    } finally {
      options.setBusy(false)
    }
  }, [cashOptions, options, persistLedger, persistSession, setTickets])

  const join = useCallback(async (nextSession: CashSession) => {
    if (!options.context || !options.isOnline) return
    options.setBusy(true)
    try {
      const joined = await joinCashSessionLifecycle(options.context, nextSession)
      persistSession(nextSession)
      persistLedger(joined.ledger)
      setTickets(joined.tickets)
      saveSessionTickets(options.context, nextSession.id, joined.tickets)
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'cash.session', step: 'join' }))
    } finally {
      options.setBusy(false)
    }
  }, [options, persistLedger, persistSession, setTickets])

  const openDefault = useCallback((openingFloatCents: number) => {
    const registerId = options.context?.defaultCashRegisterId
      ?? cashOptions.registers.find((register) => register.isActive)?.id
    if (registerId) void open(registerId, openingFloatCents)
  }, [cashOptions.registers, open, options.context?.defaultCashRegisterId])

  const printClosing = useCallback(async (closing: CashClosingRecord, printOptions: { isReprint?: boolean; copyNumber?: number; confirmedNotPrinted?: boolean } = {}) => {
    if (!options.context || printingClosingId) return false
    const state = usePrintAgentStore.getState()
    const printerId = state.selectedPrinterId || state.selectedPrinter?.id
    if (!state.token) { sileo.warning({ title: 'Servidor de impresión no configurado.' }); return false }
    if (!printerId) { sileo.warning({ title: 'No hay una impresora configurada.' }); return false }
    if (!printOptions.isReprint && closing.printStatus === 'printed') {
      sileo.warning({ title: 'Este cierre ya se imprimió.', description: 'Usa la acción de reimpresión para generar una copia.' })
      return false
    }
    if (closing.printStatus === 'unknown' && (!printOptions.isReprint || !printOptions.confirmedNotPrinted)) {
      sileo.warning({ title: 'Confirma que no se imprimió.', description: 'Comprueba físicamente el cierre antes de solicitar una copia.' })
      return false
    }
    if (closing.printStatus === 'unknown') {
      try {
        const previousStatus = await checkUnknownClosingJob(closing, state.getPrintJob)
        if (previousStatus !== 'ready') {
          sileo.warning({ title: previousStatus === 'printed' ? 'El agente confirma que ya se imprimió.' : 'La impresión anterior sigue en curso.', description: 'Espera a que termine y actualiza el histórico antes de generar otra copia.' })
          return false
        }
      } catch {
        sileo.warning({ title: 'No se pudo comprobar el trabajo anterior.', description: 'Conecta con el agente de impresión antes de generar otra copia.' })
        return false
      }
    }
    const copyNumber = printOptions.isReprint ? nextCashClosingCopyNumber(closing, printOptions.copyNumber) : 0
    const requestId = cashClosingRequestId(closing.id, Boolean(printOptions.isReprint), copyNumber)
    setPrintingClosingId(closing.id)
    try {
      const claimed = await recordCashClosingPrintResult(options.context, {
        closingId: closing.id, printerId, requestId, status: 'pending',
        isReprint: Boolean(printOptions.isReprint), copyNumber,
      })
      if (!claimed) {
        sileo.warning({ title: 'La impresión ya fue solicitada.', description: 'Actualiza el histórico antes de crear otra copia.' })
        return false
      }
      const result = await printCashClosing({ closing, context: options.context, isReprint: printOptions.isReprint, copyNumber })
      await recordCashClosingPrintResult(options.context, {
        closingId: closing.id, printerId, requestId,
        printJobId: result.job.jobId || result.job.id || null, status: 'printed',
        isReprint: Boolean(printOptions.isReprint), copyNumber,
      })
      const refreshed = await loadCashClosing(options.context, closing.id)
      setCompletedClosing((current) => current?.id === refreshed.id ? refreshed : current)
      setCashClosings((current) => current.map((item) => item.id === refreshed.id ? refreshed : item))
      return true
    } catch (error) {
      const errorCode = error && typeof error === 'object' && 'code' in error ? String(error.code) : 'PRINT_FAILED'
      const status = errorCode === 'PRINT_STATUS_UNKNOWN' ? 'unknown' : 'failed'
      const errorJob = error && typeof error === 'object' && 'details' in error ? error.details : null
      const errorJobId = errorJob && typeof errorJob === 'object'
        ? 'jobId' in errorJob ? errorJob.jobId : 'id' in errorJob ? errorJob.id : null : null
      const printJobId = typeof errorJobId === 'string' ? errorJobId : null
      try {
        await recordCashClosingPrintResult(options.context, {
          closingId: closing.id, printerId, requestId, printJobId, status, errorCode,
          isReprint: Boolean(printOptions.isReprint), copyNumber,
        })
        const refreshed = await loadCashClosing(options.context, closing.id)
        setCompletedClosing((current) => current?.id === refreshed.id ? refreshed : current)
        setCashClosings((current) => current.map((item) => item.id === refreshed.id ? refreshed : item))
      } catch (auditError) { reportOperationError(auditError, { operation: 'cash.closing.print_audit', operationId: requestId, cashSessionId: closing.id, step: 'record_result' }) }
      return false
    } finally {
      setPrintingClosingId(null)
    }
  }, [options.context, printingClosingId])

  const openClosingHistory = useCallback(async () => {
    if (!options.context || !options.isOnline) {
      options.onError('El histórico de cierres requiere conexión.')
      return
    }
    options.setBusy(true)
    try {
      setCashClosings(await loadCashClosingHistory(options.context))
      setClosingHistoryOpen(true)
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'cash.session', step: 'openClosingHistory' }))
    } finally {
      options.setBusy(false)
    }
  }, [options])

  const close = useCallback(async (payload: CashClosedPayload) => {
    if (!options.context?.canCloseCashSession || !options.isOnline) return false
    options.setBusy(true)
    try {
      await options.syncPendingEvents()
      if (getOfflineQueue().some((event) => event.tenantId === options.context?.tenantId)) {
        throw new UserFacingError('Quedan operaciones sin sincronizar en este dispositivo. Sincronízalas antes de cerrar.')
      }
      const [cashlogyBalance, stackerCollectionsCents] = await Promise.all([
        loadActiveCashlogyCashBalance(),
        loadCashlogyStackerCollectionsTotal(options.context, payload.sessionId),
      ])
      const expectedCashCents = payload.expectedCashCents
        - (stackerCollectionsCents - cashlogyClosingStackerCollectionsCents)
      const countedCashCents = cashlogyBalance?.totalCents ?? payload.countedCashCents
      const effectivePayload = {
        ...payload,
        expectedCashCents,
        countedCashCents,
        finalCashFundCents: cashlogyBalance?.totalCents ?? payload.finalCashFundCents,
        discrepancyCents: (countedCashCents - expectedCashCents)
          + (payload.countedCardCents - payload.expectedCardCents)
          + (payload.countedInvitationCents - payload.expectedInvitationCents)
          + (payload.countedOtherCents - payload.expectedOtherCents),
      }
      const closing = await closeCashSessionLifecycle(options.context, effectivePayload)
      persistSession(null)
      setLedger([])
      clearSaleLedger(options.context)
      clearSessionTickets(options.context, payload.sessionId)
      setTickets([])
      setCloseModalOpen(false)
      setCashlogyClosingCashCents(null)
      setCashlogyClosingStackerCollectionsCents(0)
      options.refreshPendingCount()
      await cashOptions.refresh(options.context)
      setCompletedClosing(closing)
      if (usePrintAgentStore.getState().preferences.printCashClosingAutomatically) {
        await printClosing(closing)
      }
      return true
    } catch (error) {
      options.onError(getReadableError(error, { operation: 'cash.session', step: 'close' }))
      return false
    } finally {
      options.setBusy(false)
    }
  }, [cashOptions, cashlogyClosingStackerCollectionsCents, options, persistSession, printClosing, setTickets])

  const openCloseModal = useCallback(async () => {
    if (!cashContext || !session) return false
    setGlobalBusy(true)
    reportError(null)
    try {
      await syncPendingEvents()
      if (getOfflineQueue().some((event) => event.tenantId === cashContext.tenantId)) {
        throw new UserFacingError('Quedan operaciones sin sincronizar en este dispositivo. Sincronízalas antes de cerrar.')
      }
      const [openOrders, nextLedger, cashlogyBalance, stackerCollectionsCents] = await Promise.all([
        loadOpenRestaurantOrders(cashContext),
        loadSalesLedgerFromSupabase(cashContext, session.id),
        loadActiveCashlogyCashBalance(),
        loadCashlogyStackerCollectionsTotal(cashContext, session.id),
      ])
      setOpenOrderCount(openOrders.length)
      persistLedger(nextLedger)
      setCashlogyClosingCashCents(cashlogyBalance?.totalCents ?? null)
      setCashlogyClosingStackerCollectionsCents(stackerCollectionsCents)
      setCloseModalOpen(true)
      return true
    } catch (error) {
      reportError(getReadableError(error, { operation: 'cash.session', step: 'openCloseModal' }))
      return false
    } finally {
      setGlobalBusy(false)
    }
  }, [cashContext, reportError, session, setGlobalBusy, syncPendingEvents, persistLedger])

  const reset = useCallback(() => {
    const closed = getClosedCashState()
    setSession(closed.session)
    setLedger([...closed.ledger])
    setTickets([...closed.tickets])
    setCloseModalOpen(false)
    setCashlogyClosingCashCents(null)
    setCashlogyClosingStackerCollectionsCents(0)
    setHistoryOpen(false)
    setClosingHistoryOpen(false)
    setCashClosings([])
    setCompletedClosing(null)
  }, [setTickets])

  const hydrate = useCallback((
    nextSession: CashSession | null,
    nextLedger: SaleRecord[],
    nextTickets: SessionTicketRecord[],
  ) => {
    setSession(nextSession)
    setLedger(nextLedger)
    setTickets(nextTickets)
    setClosingHistoryOpen(false)
    setCashClosings([])
    setCompletedClosing(null)
    setCashlogyClosingCashCents(null)
    setCashlogyClosingStackerCollectionsCents(0)
  }, [setTickets])

  const clearRejectedSession = useCallback((closedSessionId: string) => {
    if (!options.context) return
    setSession(null)
    saveCachedCashSession(options.context, null)
    setLedger([])
    clearSaleLedger(options.context)
    clearSessionTickets(options.context, closedSessionId)
    setTickets([])
    setCloseModalOpen(false)
    setCashlogyClosingCashCents(null)
    setCashlogyClosingStackerCollectionsCents(0)
    setHistoryOpen(false)
  }, [options.context, setTickets])

  return {
    clearRejectedSession,
    close,
    closeModalOpen,
    openOrderCount,
    closingHistoryOpen,
    cashClosings,
    cashlogyClosingCashCents,
    cashlogyClosingStackerCollectionsCents,
    completedClosing,
    hydrate,
    historyOpen,
    join,
    ledger,
    mergeRemotePrintStates,
    movementModalOpen,
    movementSaving,
    movements,
    open,
    openCloseModal,
    openClosingHistory,
    openDefault,
    options: cashOptions,
    persistLedger,
    persistTickets,
    printSale,
    printClosing,
    printingClosingId,
    refreshConfirmedSale,
    refreshLedger,
    registerMovement,
    reset,
    session,
    setCloseModalOpen: (open: boolean) => {
      setCloseModalOpen(open)
      if (!open) {
        setCashlogyClosingCashCents(null)
        setCashlogyClosingStackerCollectionsCents(0)
      }
    },
    setClosingHistoryOpen,
    setCompletedClosing,
    setHistoryOpen,
    setLedger,
    setMovementModalOpen,
    setSession,
    setTickets,
    summary: useMemo(() => summarizeSales(session?.openingFloatCents ?? 0, ledger, movements), [ledger, movements, session?.openingFloatCents]),
    ticketActions,
    tickets,
  }
}
